/**
 * HiddenRewardsSection — «Skjulte belønninger» i Admin-panelet (#219).
 *
 * Et nytt ord i allergenleksikonet (#205) kan skjule en `mat`-belønning som
 * før var synlig, for et medlem med den allergien (lag 2), og gi 422 ved
 * innløsning (lag 3). Seksjonen viser familieadmin hvilke belønninger det
 * gjelder, for hvem og hvilket allergen og nøkkelord som slo til. Bare for
 * familieadmin; systemadmin ser ikke innhold (D3).
 *
 * Det finnes ingen egen redigeringsflate for belønninger, så «Endre tittel»
 * åpner et felt i raden (PATCH /api/rewards/:id). Lag 1 kjører igjen på
 * serveren; en tittel som fortsatt treffer noens allergi gir en feil i raden.
 * PATCH krever fersk adminopplåsing: en 403 for utløpt opplåsing åpner
 * AdminReauthModal og prøver igjen, som CosmeticAdminSection.
 */
import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ALLERGY_CHIPS, type AllergenHit, type AllergyHiddenReward } from '@stell/shared';
import { Button, ErrorState, LoadingState } from '../../components/primitives';
import { chipLabels } from '../shopping/useHouseholdDietary';
import { AdminReauthModal } from './AdminReauthModal';
import { ApiError, isAdminScopeError } from './useMembers';
import { useHiddenRewards, useRenameReward } from './useHiddenRewards';

export function HiddenRewardsSection(): ReactElement {
  const { t } = useTranslation();
  const list = useHiddenRewards();
  const rename = useRenameReward();
  const fieldId = useId();

  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<(() => void) | null>(null);

  /** Kjente brikker får visningsnavnet sitt; en egendefinert allergi står som den er (chipLabels). */
  const hitLabel = (h: AllergenHit): string =>
    t('admin.hiddenRewards.hit', {
      allergen: chipLabels([h.allergen], ALLERGY_CHIPS, (k) => t(`allergy.chip.${k}`))[0],
      keyword: h.keyword,
    });

  function save(id: string, title: string): void {
    setError(null);
    rename.mutate(
      { id, title },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => {
          if (isAdminScopeError(err)) {
            setRetry(() => () => save(id, title));
            return;
          }
          setError(err instanceof ApiError && err.code === 'reward_authoring_blocked' ? 'blocked' : 'generic');
        },
      },
    );
  }

  function startEdit(r: AllergyHiddenReward): void {
    setError(null);
    setEditing({ id: r.reward_id, title: r.title });
  }

  const rows = list.data ?? [];

  return (
    <div className="space-y-3" data-testid="hidden-rewards-section">
      <p className="m-0 text-sm text-ink-2">{t('admin.hiddenRewards.explain')}</p>

      {list.isLoading ? (
        <LoadingState lines={2} />
      ) : list.isError ? (
        <ErrorState
          variant="inline"
          heading={t('admin.hiddenRewards.load_error')}
          onRetry={() => void list.refetch()}
        />
      ) : rows.length === 0 ? (
        <p className="m-0 text-sm text-ink-2" data-testid="hidden-rewards-empty">
          {t('admin.hiddenRewards.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none divide-y divide-card-line p-0" data-testid="hidden-rewards-list">
          {rows.map((r) => {
            const isEditing = editing?.id === r.reward_id;
            return (
              <li key={r.reward_id} data-testid={`hidden-reward-${r.reward_id}`} className="space-y-2 py-3">
                <p className="m-0 text-sm font-bold text-ink">{r.title}</p>
                <ul className="m-0 list-none space-y-0.5 p-0">
                  {r.hidden_for.map((m) => (
                    <li key={m.member_id} className="text-caption text-ink-2">
                      {t('admin.hiddenRewards.hiddenFor', {
                        name: m.short_name,
                        hits: m.hits.map(hitLabel).join(', '),
                      })}
                    </li>
                  ))}
                </ul>

                {isEditing ? (
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const title = editing.title.trim();
                      if (title) save(r.reward_id, title);
                    }}
                  >
                    <label htmlFor={`${fieldId}-${r.reward_id}`} className="block text-sm text-ink-2">
                      {t('admin.hiddenRewards.titleLabel')}
                    </label>
                    <input
                      id={`${fieldId}-${r.reward_id}`}
                      data-testid={`hidden-reward-title-${r.reward_id}`}
                      value={editing.title}
                      maxLength={120}
                      onChange={(e) => setEditing({ id: r.reward_id, title: e.target.value })}
                      className="field"
                    />
                    {error ? (
                      <p role="alert" className="m-0 text-sm text-bad" data-testid="hidden-reward-error">
                        {t(error === 'blocked' ? 'admin.hiddenRewards.renameBlocked' : 'rewards.admin.error.generic')}
                      </p>
                    ) : null}
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setEditing(null)} data-testid="hidden-reward-cancel">
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={rename.isPending || !editing.title.trim()}
                        data-testid="hidden-reward-save"
                      >
                        {t('common.save')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => startEdit(r)}
                    data-testid={`hidden-reward-edit-${r.reward_id}`}
                  >
                    {t('admin.hiddenRewards.edit')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AdminReauthModal
        open={retry !== null}
        onClose={() => setRetry(null)}
        onSuccess={() => {
          const again = retry;
          setRetry(null);
          again?.();
        }}
      />
    </div>
  );
}
